import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Input from '../common/Input';
import Button from '../common/Button';
import { useAuth } from '../../hooks/useAuth';
import { RegisterData } from '../../types';

interface RegisterFormProps {
  redirectTo?: string;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ redirectTo }) => {
  const { register, isLoading } = useAuth();
  const [formData, setFormData] = useState<RegisterData>({
    nome: '',
    email: '',
    senha: '',
    confirmarSenha: '',
    telefone: '',
    tipo: 'comprador',
    aceitouTermos: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    } else if (formData.nome.trim().length < 3) {
      newErrors.nome = 'Nome deve ter pelo menos 3 caracteres';
    }

    if (!formData.email) {
      newErrors.email = 'E-mail é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'E-mail inválido';
    }

    if (!formData.senha) {
      newErrors.senha = 'Senha é obrigatória';
    } else if (formData.senha.length < 6) {
      newErrors.senha = 'Senha deve ter pelo menos 6 caracteres';
    }

    if (formData.senha !== formData.confirmarSenha) {
      newErrors.confirmarSenha = 'As senhas não coincidem';
    }

    if (formData.telefone && !/^\(\d{2}\) \d{4,5}-\d{4}$/.test(formData.telefone)) {
      newErrors.telefone = 'Telefone inválido';
    }

    if (!formData.aceitouTermos) {
      newErrors.aceitouTermos = 'Você deve aceitar os termos de uso';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    await register(formData, redirectTo);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return `(${numbers}`;
    if (numbers.length <= 6) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    if (numbers.length <= 10) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setFormData((prev) => ({ ...prev, telefone: formatted }));
    if (errors.telefone) {
      setErrors((prev) => ({ ...prev, telefone: '' }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <Input
        label="Nome completo"
        type="text"
        name="nome"
        placeholder="Seu nome completo"
        value={formData.nome}
        onChange={handleChange}
        error={errors.nome}
        leftIcon={
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        }
        disabled={isLoading}
        required
      />

      <Input
        label="E-mail"
        type="email"
        name="email"
        placeholder="seu@email.com"
        value={formData.email}
        onChange={handleChange}
        error={errors.email}
        leftIcon={
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        }
        autoComplete="email"
        disabled={isLoading}
        required
      />

      <Input
        label="Telefone (opcional)"
        type="tel"
        name="telefone"
        placeholder="(11) 99999-9999"
        value={formData.telefone}
        onChange={handlePhoneChange}
        error={errors.telefone}
        leftIcon={
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        }
        disabled={isLoading}
      />

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Senha"
          type={showPassword ? 'text' : 'password'}
          name="senha"
          placeholder="••••••••"
          value={formData.senha}
          onChange={handleChange}
          error={errors.senha}
          leftIcon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          }
          disabled={isLoading}
          required
        />

        <Input
          label="Confirmar senha"
          type={showPassword ? 'text' : 'password'}
          name="confirmarSenha"
          placeholder="••••••••"
          value={formData.confirmarSenha}
          onChange={handleChange}
          error={errors.confirmarSenha}
          disabled={isLoading}
          required
        />
      </div>

      <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600">
        <input
          type="checkbox"
          checked={showPassword}
          onChange={() => setShowPassword(!showPassword)}
          className="w-4 h-4 rounded border-gray-300 text-green-700 focus:ring-green-500"
        />
        Mostrar senhas
      </label>

      {/* Tipo de conta */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Tipo de conta
        </label>
        <div className="grid grid-cols-2 gap-4">
          <label
            className={`relative flex flex-col items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
              formData.tipo === 'comprador'
                ? 'border-green-600 bg-green-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <input
              type="radio"
              name="tipo"
              value="comprador"
              checked={formData.tipo === 'comprador'}
              onChange={handleChange}
              className="sr-only"
            />
            <span className="text-3xl mb-2">🛒</span>
            <span className="font-semibold text-gray-900">Comprador</span>
            <span className="text-xs text-gray-500 text-center mt-1">
              Buscar e comprar
            </span>
            {formData.tipo === 'comprador' && (
              <span className="absolute top-2 right-2 w-5 h-5 bg-green-600 rounded-full flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </span>
            )}
          </label>

          <label
            className={`relative flex flex-col items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
              formData.tipo === 'vendedor'
                ? 'border-green-600 bg-green-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <input
              type="radio"
              name="tipo"
              value="vendedor"
              checked={formData.tipo === 'vendedor'}
              onChange={handleChange}
              className="sr-only"
            />
            <span className="text-3xl mb-2">🏪</span>
            <span className="font-semibold text-gray-900">Vendedor</span>
            <span className="text-xs text-gray-500 text-center mt-1">
              Anunciar e vender
            </span>
            {formData.tipo === 'vendedor' && (
              <span className="absolute top-2 right-2 w-5 h-5 bg-green-600 rounded-full flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </span>
            )}
          </label>
        </div>
      </div>

      {/* Termos */}
      <div>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            name="aceitouTermos"
            checked={formData.aceitouTermos}
            onChange={handleChange}
            className="w-4 h-4 mt-0.5 rounded border-gray-300 text-green-700 focus:ring-green-500"
          />
          <span className="text-sm text-gray-600">
            Li e aceito os{' '}
            <Link to="/termos" className="text-green-700 hover:underline font-medium">
              Termos de Uso
            </Link>{' '}
            e a{' '}
            <Link to="/privacidade" className="text-green-700 hover:underline font-medium">
              Política de Privacidade
            </Link>
          </span>
        </label>
        {errors.aceitouTermos && (
          <p className="mt-1 text-sm text-red-600">{errors.aceitouTermos}</p>
        )}
      </div>

      <Button
        type="submit"
        fullWidth
        isLoading={isLoading}
        size="lg"
      >
        Criar Conta
      </Button>

      <p className="text-center text-gray-600">
        Já tem uma conta?{' '}
        <Link to="/login" className="text-green-700 hover:text-green-800 font-semibold">
          Faça login
        </Link>
      </p>
    </form>
  );
};

export default RegisterForm;
